import { getFullURL } from './endpoints';
import { emitNotification } from '../components/Notification';
import i18next from 'i18next';
import { LogoutOptions, useAuth0 } from '@auth0/auth0-react';
import { useMemo } from 'react';
import { ToastOptions } from 'react-toastify';
import { NavigateFunction, useNavigate } from 'react-router';
import { ErrorKey } from '../../../shared/utils/errorKey';
import { BaseDto } from '../../../shared/utils/base.dto';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'OPTIONS';

export type APIOptions = {
  token?: string;
  body?: unknown;
  method?: HttpMethod;
  type?: string;
  queryParameters?: URLSearchParams;
};

export type APICallbacks = {
  logout?: (options?: LogoutOptions) => Promise<void>;
};

export type BlobResult = {
  blob: Blob;
  fileName: string;
  contentType: string;
};

export class ApiError extends Error {
  public readonly status?: number;
  public readonly errorKey?: string;
  public readonly responseBody?: unknown;
  public readonly isAuthError: boolean;
  public readonly isNotified: boolean;

  public constructor(params: { message: string; status?: number; errorKey?: string; responseBody?: unknown; isAuthError?: boolean; isNotified?: boolean }) {
    super(params.message);
    this.name = 'ApiError';
    this.isNotified = params.isNotified ?? false;
    this.status = params.status;
    this.errorKey = params.errorKey;
    this.responseBody = params.responseBody;
    this.isAuthError = params.isAuthError ?? false;
  }
}

export type AuthFetch = <T = unknown>(path: string, options?: APIOptions) => Promise<T>;

export type AuthFetchBlob = (path: string, options?: APIOptions) => Promise<BlobResult>;

const DEFAULT_ERROR_TOAST: ToastOptions = {
  type: 'error',
  position: 'top-right',
};

function buildBaseHeaders(): Record<string, string> {
  return {
    'Cache-Control': 'no-cache',
    'User-Language': i18next.language,
    'x-date': new Date().toISOString(),
    Pragma: 'no-cache',
    Expires: '0',
  };
}

function withAuthHeader(headers?: HeadersInit, token?: string): HeadersInit | undefined {
  return token ? { ...headers, Authorization: `Bearer ${token}` } : headers;
}

function isJsonResponse(contentType: string | null): boolean {
  return (contentType ?? '').toLowerCase().includes('application/json');
}

function extractFileNameFromContentDisposition(header: string | null): string | null {
  if (!header) {
    return null;
  }

  const quoted: RegExpMatchArray | null = header.match(/filename="([^"]+)"/i);
  if (quoted?.[1]) {
    return quoted[1];
  }

  const utf8: RegExpMatchArray | null = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      return utf8[1];
    }
  }

  const unquoted: RegExpMatchArray | null = header.match(/filename=([^;]+)/i);
  return unquoted?.[1]?.trim() ?? null;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function isBaseDto(value: unknown): value is BaseDto<unknown> {
  return typeof value === 'object' && value !== null && 'data' in value && 'error' in value;
}

function handleInternalServerError(): void {
  emitNotification(i18next.t('errorMessageTemplate.genericMsg'), DEFAULT_ERROR_TOAST);
}

async function handleAuthError(callbacks?: APICallbacks): Promise<void> {
  emitNotification(i18next.t('errorMessageTemplate.authError'), DEFAULT_ERROR_TOAST);
  await logout(callbacks);
}

function handleDomainError(error: string): boolean {
  if (!error || error === 'key_NoProfileFound') {
    return false;
  }

  if (error.startsWith('key_')) {
    emitNotification(i18next.t(`errorMessageTemplate.${error}`), DEFAULT_ERROR_TOAST);
  } else {
    emitNotification(error, DEFAULT_ERROR_TOAST);
  }

  return true;
}

export function isNotifiedError(error: unknown): boolean {
  return error instanceof ApiError && error.isNotified;
}

function buildRequestInit(options?: APIOptions): RequestInit {
  const method: HttpMethod = options?.method ?? 'GET';
  const headers: Record<string, string> = buildBaseHeaders();

  if (options?.body instanceof FormData) {
    return {
      method,
      headers,
      body: options.body,
    };
  }

  if (options?.body !== undefined) {
    return {
      method,
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(options.body),
    };
  }

  return { method, headers };
}

export function downloadBlob(blob: Blob, fileName: string): void {
  const url: string = URL.createObjectURL(blob);
  const a: HTMLAnchorElement = document.createElement('a');

  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);

  try {
    a.click();
  } finally {
    a.remove();
    URL.revokeObjectURL(url);
  }
}

export const logout = async (callbacks?: APICallbacks): Promise<void> => {
  await callbacks?.logout?.();
};

async function throwApiError(response: Response, callbacks?: APICallbacks): Promise<never> {
  if (response.status === 401) {
    await handleAuthError(callbacks);
    throw new ApiError({ message: 'Unauthorized', status: 401, isAuthError: true, isNotified: true });
  }
  
  const contentType: string | null = response.headers.get('content-type');
  
  if (isJsonResponse(contentType)) {
    const text: string = await response.text();
    
    if (text) {
      const parsed: unknown = safeJsonParse(text);
      
      if (isBaseDto(parsed) && parsed.error) {
        let isNotified: boolean = true;

        if (parsed.error.error === ErrorKey.InternalServerError) {
          handleInternalServerError();
        } else {
          isNotified = handleDomainError(parsed.error.error);
        }

        throw new ApiError({
          message: parsed.error.error,
          status: response.status,
          errorKey: parsed.error.error,
          responseBody: parsed,
          isNotified,
        });
      }

      const isServerError: boolean = response.status === 500;
      if (isServerError) {
        handleInternalServerError();
      }

      throw new ApiError({
        message: `HTTP error ${response.status}`,
        status: response.status,
        responseBody: parsed,
        isNotified: isServerError,
      });
    }
  }

  handleInternalServerError();

  throw new ApiError({
    message: `HTTP error ${response.status}`,
    status: response.status,
    isNotified: true,
  });
}

function parseJsonResponse<T>(text: string): T {
  const parsed: unknown = safeJsonParse(text);
  
  if (isBaseDto(parsed)) {
    if (parsed.error) {
      let isNotified: boolean = true;

      if (parsed.error.error === ErrorKey.InternalServerError) {
        handleInternalServerError();
      } else {
        isNotified = handleDomainError(parsed.error.error);
      }

      throw new ApiError({
        message: parsed.error.error,
        errorKey: parsed.error.error,
        responseBody: parsed,
        isNotified,
      });
    }
    
    return parsed.data as T;
  }
  
  return parsed as T;
}



export async function fetchFromRestAPI<T = unknown>(
  path: string,
  options?: APIOptions,
  callbacks?: APICallbacks
): Promise<T> {
  const init: RequestInit = buildRequestInit(options);
  init.headers = withAuthHeader(init.headers, options?.token);

  const response: Response = await fetch(
    getFullURL(path, options?.type, options?.queryParameters),
    init
  );
  
  if (!response.ok) {
    await throwApiError(response, callbacks);
  }
  
  if (response.status === 204) {
    return undefined as T;
  }
  
  const contentType: string | null = response.headers.get('content-type');
  const text: string = await response.text();

  if (!text) {
    throw new ApiError({
      message: 'Response body is empty',
      status: response.status,
    });
  }

  if (isJsonResponse(contentType)) {
    return parseJsonResponse<T>(text);
  }

  return text as T;
}

export async function fetchBlobFromRestAPI(
  path: string,
  options?: APIOptions,
  callbacks?: APICallbacks
): Promise<BlobResult> {
  const init: RequestInit = buildRequestInit(options);
  init.headers = withAuthHeader(init.headers, options?.token);

  const response: Response = await fetch(
    getFullURL(path, options?.type, options?.queryParameters),
    init
  );

  if (!response.ok) {
    await throwApiError(response, callbacks);
  }

  const blob: Blob = await response.blob();
  const contentType: string = response.headers.get('content-type') ?? '';
  const contentDisposition: string | null = response.headers.get('content-disposition');
  const fileName: string | null = extractFileNameFromContentDisposition(contentDisposition);

  return {
    blob,
    fileName: fileName ?? 'file',
    contentType,
  };
}

export const useAuthFetchFromRestAPI = (): {
  authFetch: AuthFetch;
  authFetchBlob: AuthFetchBlob;
} => {
  const { getAccessTokenSilently, logout: auth0Logout } = useAuth0();
  const navigate: NavigateFunction = useNavigate();

  const callbacks: APICallbacks = useMemo(
    () => ({
      logout: async (options?: LogoutOptions): Promise<void> => {
        await auth0Logout(options ?? { logoutParams: { returnTo: window.location.origin } });
        navigate('/');
      },
    }),
    [auth0Logout, navigate]
  );

  const authFetch: AuthFetch = useMemo(() => {
    return async <T = unknown>(path: string, options?: APIOptions): Promise<T> => {
      const token: string = options?.token ?? (await getAccessTokenSilently());
      return fetchFromRestAPI<T>(path, { ...options, token }, callbacks);
    };
  }, [getAccessTokenSilently, callbacks]);

  const authFetchBlob: AuthFetchBlob = useMemo(() => {
    return async (path: string, options?: APIOptions): Promise<BlobResult> => {
      const token: string = options?.token ?? (await getAccessTokenSilently());
      return fetchBlobFromRestAPI(path, { ...options, token }, callbacks);
    };
  }, [getAccessTokenSilently, callbacks]);

  return { authFetch, authFetchBlob };
};
